import { ICalloutContentStyles, IPlainCardProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLabel, IPPrimaryButton, IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPComboBox } from 'baseComponents/Combobox/IPComboBox';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { AuthSchemaInfo } from 'components/Auth/AuthSchemaInfo';
import AuthSchemaList from 'components/Auth/AuthSchemaList';
import HrefLink from 'components/Links/HrefLink';
import WarningMessage from 'components/Notification/WarningMessage';
import { globalStackTokensGapSmall } from 'globalStyles';
import { AuthSchema } from 'models/auth/authSchema';
import { Fragment, useContext, useEffect, useState } from 'react';

import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPHoverCard, IPHoverCardType } from 'baseComponents/HoverCard/IPHoverCard';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { useTranslation } from 'react-i18next';
import { apiGetAuthSchemasForFilter } from 'services/Api/authService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { sortOnString } from 'utils/sorting';

interface IEventFormAuthSchemaPickerProps {
  selectedAuthSchemaId: number | undefined;
  disabled?: boolean;
  label?: string;
  hideMembersOnHover?: boolean;
  showForUserId?: string;
  isOwner?: boolean;
  overrideAdmin?: boolean;
  onSelect: (item: AuthSchema | undefined) => void;
}

const AuthSchemaPicker = (props: IEventFormAuthSchemaPickerProps) => {
  const { t } = useTranslation(['translation', 'adminAuth']);
  const appContext = useContext(AppContext);
  const [authSchemas, setAuthSchemas] = useState<AuthSchema[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedAuthSchema, setSelectedAuthSchema] = useState<AuthSchema | undefined>(undefined);
  const [currentAuthSchema, setCurrentAuthSchema] = useState<AuthSchema | undefined>(undefined);
  const disabledCalc = (!appContext.user.isAdmin() || props.disabled) && !props.overrideAdmin;
  const url = '/admin/users/permissions';

  //
  // Effects
  //
  useEffect(() => {
    if (isOpen) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    //for displaying the current schema, use the global data cache
    //the cache always contains the schema that is linked to an entity that the user see.
    if (props.selectedAuthSchemaId) {
      setCurrentAuthSchema(appContext.globalDataCache.authSchemas.get(props.selectedAuthSchemaId));
    } else {
      setCurrentAuthSchema(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedAuthSchemaId]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setSelectedAuthSchema(undefined);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const schemas = await apiGetAuthSchemasForFilter(props.showForUserId, accessToken);
      schemas.sort((a, b) => sortOnString(a.name, b.name));
      setAuthSchemas(schemas);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const calloutStyle: Partial<ICalloutContentStyles> = {
    root: {
      padding: '10px 12px',
      minHeight: 300,
      height: '60vh',
      maxHeight: '80vh',
      minWidth: 250,
      width: '40vw',
      maxWidth: '80vw',
    },
  };

  const onSelect = (item: AuthSchema | undefined) => {
    props.onSelect(item);
    setCurrentAuthSchema(item);
    setIsOpen(false);
  };

  const getButtonText = (): string => {
    if (selectedAuthSchema) {
      return t('translation:General.Button.Select');
    }
    if (props.selectedAuthSchemaId) {
      return t('translation:General.Button.Clear');
    } else {
      return t('translation:General.Button.Close');
    }
  };

  const onRenderSchemaInfo = (item: AuthSchema | undefined): JSX.Element | null => {
    if (!item) return null;

    return (
      <IPStack styles={{ root: { padding: 20, minHeight: 300, minWidth: 250, height: '30vh', width: '50vw' } }}>
        <AuthSchemaInfo authSchemaId={item.authSchemaId} showNoMemberMsg={true} />
      </IPStack>
    );
  };

  const plainCardProps: IPlainCardProps = {
    onRenderPlainCard: onRenderSchemaInfo,
    renderData: currentAuthSchema,
    directionalHint: IPDirectionalHint.topCenter,
  };

  const showCallout = () => {
    if (!disabledCalc) setIsOpen(true);
  };

  const getNoDataLink = () => {
    if (isLoading || authSchemas.length > 0) return null;

    if (window.location.pathname !== url) {
      if (appContext.user.isAdmin() && !props.showForUserId) {
        return (
          <IPStack verticalFill verticalAlign="center" horizontalAlign="center">
            <HrefLink href={url}>{t('adminAuth:AuthSchemaPicker.NoDataLinkAdmin')}</HrefLink>
          </IPStack>
        );
      }

      if (props.showForUserId) {
        return (
          <IPStack verticalFill verticalAlign="center" horizontalAlign="center">
            <IPText>
              {t('adminAuth:AuthSchemaPicker.NoDataLinkUser', {
                name: appContext.globalDataCache.users.get(props.showForUserId).name,
              })}
            </IPText>
          </IPStack>
        );
      }
    }

    return (
      <IPStack verticalFill verticalAlign="center" horizontalAlign="center">
        <IPText>{t('adminAuth:AuthSchemaPicker.NoDataLink')}</IPText>
      </IPStack>
    );
  };

  //
  // Main render
  //
  if (!hasUserFeature(appContext, FeatureTypes.RBAC)) {
    return null;
  }

  //Show warning if the user is not an owner and does not have access to the schema.
  //The global data cache always contains the calculated lines for the current user.
  const isNoOwnerOrAdminOrRead =
    selectedAuthSchema &&
    !props.isOwner &&
    !appContext.user.isAdmin() &&
    !appContext.globalDataCache.authSchemas.get(selectedAuthSchema.authSchemaId).hasLine(appContext.user.id);

  return (
    <Fragment>
      <IPHoverCard
        type={IPHoverCardType.plain}
        plainCardProps={plainCardProps}
        shouldBlockHoverCard={() => props.hideMembersOnHover || currentAuthSchema === undefined}
      >
        <IPComboBox
          id="authschema-target"
          text={currentAuthSchema?.name ?? t('adminAuth:AuthSchemaPicker.Placeholder')}
          options={[]}
          onMenuOpen={showCallout}
          onKeyDown={showCallout}
          onKeyDownCapture={showCallout}
          disabled={disabledCalc}
          placeholder={t('adminAuth:AuthSchemaPicker.Placeholder')}
          label={props.label ?? t('adminAuth:AuthSchemaPicker.Label')}
          styles={{ root: { minWidth: 150 } }}
        />
      </IPHoverCard>
      <IPCallout
        styles={calloutStyle}
        hidden={!isOpen}
        setInitialFocus
        gapSpace={0}
        beakWidth={0}
        target={'#authschema-target'}
        onDismiss={() => setIsOpen(false)}
      >
        <IPStack verticalFill tokens={globalStackTokensGapSmall}>
          {getNoDataLink()}
          {isLoading && (
            <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
              <IPSpinner size={IPSpinnerSize.large} />
            </IPStack>
          )}
          {!isLoading && authSchemas.length > 0 && props.showForUserId && (
            <IPStack.Item>
              <IPLabel>
                {t('adminAuth:AuthSchemaPicker.FilterUserInfo', {
                  name: appContext.globalDataCache.users.get(props.showForUserId).name,
                })}
              </IPLabel>
            </IPStack.Item>
          )}
          {!isLoading && authSchemas.length > 0 && (
            <IPStack.Item grow>
              <AuthSchemaList
                authSchemas={authSchemas}
                onSelectAuthSchema={(item) => {
                  setSelectedAuthSchema(item);
                }}
                onChooseAuthSchema={(item) => {
                  onSelect(item);
                }}
                isLoading={isLoading}
                showSearch={true}
              />
            </IPStack.Item>
          )}
          {selectedAuthSchema && !appContext.isMobileView && (
            <IPStack.Item>
              <AuthSchemaInfo
                authSchemaId={selectedAuthSchema.authSchemaId}
                showNoMemberMsg={!isNoOwnerOrAdminOrRead}
              />
            </IPStack.Item>
          )}
          {isNoOwnerOrAdminOrRead && <WarningMessage message={t('adminAuth:AuthSchemaPicker.NoMemberWarning')} />}
          <IPSeparator />
          <IPStack.Item>
            <IPPrimaryButton
              onClick={() => {
                onSelect(selectedAuthSchema);
              }}
            >
              {getButtonText()}
            </IPPrimaryButton>
          </IPStack.Item>
        </IPStack>
      </IPCallout>
    </Fragment>
  );
};

export default AuthSchemaPicker;
